import React, { useState } from 'react';
import { useAuth } from '../components/AuthContext'; 
import { useNavigate } from 'react-router-dom'; 

// IMPORTS DES COMPOSANTS MODULAIRES
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import NotificationPanel from '../components/NotificationPanel';
import DocumentPreviewModal from '../components/DocumentPreviewModal';
import TemplateComposer from '../components/TemplateComposer';

// Import du hook personnalisé pour les documents
import { useDocumentTemplates } from '../hooks/useDocumentTemplates';
import { useDocumentPreview } from '../hooks/useDocumentPreview';

const Documents: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    
    // Hook pour gérer les documents depuis le backend
    const {
        documents,
        loading,
        uploading,
        downloadDocument,
        deleteDocument,
        updateDocument,
        refetch,
    } = useDocumentTemplates();

    // Hook pour l'aperçu de documents
    const { previewState, openPreview, closePreview } = useDocumentPreview();

    // État pour contrôler l'ouverture/fermeture du panneau de notifications
    const [isNotifPanelOpen, setIsNotifPanelOpen] = useState(false);
    


    // États pour le modal d'édition
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingDocument, setEditingDocument] = useState<any | null>(null);
    const [editName, setEditName] = useState('');
    const [editFile, setEditFile] = useState<File | null>(null);

    // État pour le filtre de recherche
    const [searchQuery, setSearchQuery] = useState('');

    // Protection de la route
    if (!user) {
        navigate('/login', { replace: true });
        return null;
    }

    // Filtrer les documents selon la recherche
    const filteredDocuments = documents.filter(doc => 
        doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.fileName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Télécharger un document
    const handleDownloadDocument = async (doc: any) => {
        const result = await downloadDocument(doc);
        if (!result.success) {
            alert(`Erreur: ${result.error}`);
        }
    };

    // Prévisualiser un document
    const handlePreviewDocument = (doc: any) => {
        const filename = doc.relative_path || doc.fileName;
        // Si relative_path contient déjà le dossier (templates/ ou avenants/), on le garde tel quel
        const path = filename.includes('/') ? filename : `templates/${filename}`;
        const url = `documents/${encodeURIComponent(path)}/preview/`;
        console.log('🔍 Preview URL:', url);
        console.log('📄 Document:', doc);
        openPreview(
            url,
            filename, // Utiliser filename qui contient l'extension
            'simple'
        );
    };

    // Supprimer un document
    const handleDeleteDocument = async (filename: string, docName: string) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le document "${docName}" ?`)) {
            return;
        }
        
        const result = await deleteDocument(filename);
        if (!result.success) {
            alert(`Erreur: ${result.error}`);
        }
    };

    // Ouvrir le modal d'édition
    const handleOpenEditModal = (doc: any) => {
        setEditingDocument(doc);
        setEditName(doc.name);
        setEditFile(null);
        setIsEditModalOpen(true);
    };

    // Fermer le modal d'édition
    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setEditingDocument(null);
        setEditName('');
        setEditFile(null);
    };

    // Gérer le changement de fichier dans le modal d'édition
    const handleEditFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.name.endsWith('.docx')) {
                alert('Seuls les fichiers .docx sont acceptés');
                return;
            }
            setEditFile(file);
        }
    };

    // Sauvegarder les modifications
    const handleSaveEdit = async () => {
        if (!editingDocument || !editName.trim()) return;

        // Utiliser relative_path si disponible, sinon fileName
        const identifier = editingDocument.relative_path || editingDocument.fileName;
        const result = await updateDocument(identifier, editName, editFile || undefined);
        
        if (result.success) {
            alert('Document mis à jour avec succès !');
            handleCloseEditModal();
        } else {
            alert(`Erreur: ${result.error}`);
        }
    };

    return (
        <div className={`flex min-h-screen bg-base-200 ${isNotifPanelOpen ? 'overflow-hidden' : ''}`}>
            
            {/* -------------------- Sidebar (Composant Modulaire) -------------------- */}
            <Sidebar />

            {/* -------------------- Contenu Principal -------------------- */}
            <main className="flex-1 p-6 overflow-y-auto">
                
                {/* Navbar Supérieure (Composant Modulaire) */}
                <Navbar onOpenNotifPanel={() => setIsNotifPanelOpen(true)} />

                {/* Nouvel en-tête avec le même design que BulletinDePaie */}
                <div className="flex flex-col gap-6 mb-6">
                    <div className="flex justify-between items-center">
                        <h2 className="text-3xl font-extrabold text-base-content">Gestion des Documents</h2>
                        <div className="text-sm text-base-content/70">
                            {/* Vous pouvez ajouter un compteur ou autre information ici si nécessaire */}
                        </div>
                    </div>
                </div>

                {/* Contenu principal de la page Documents */}
                <div className="grid grid-cols-1 gap-6">

                    <TemplateComposer onSaved={refetch} />

                    {/* Section Documents enregistrés avec style warning et blanc */}
                    <div className="bg-white rounded-2xl shadow-xl border border-base-300 overflow-hidden">
                        {/* Header avec warning */}
                        <div className="p-6">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <div className="p-3 bg-white/20 rounded-xl">
                                        
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold">Documents enregistrés</h2>
                                        
                                    </div>
                                </div>
                                {documents.length > 0 && (
                                    <span className="px-4 py-2 bg-white/20 backdrop-blur-sm text-warning-content font-bold rounded-full text-sm border border-white/30">
                                        {documents.length} document{documents.length > 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>
                        </div>
                        
                        <div className="p-6">
                            {/* Barre de recherche et actions */}
                            <div className="mb-6 flex flex-col sm:flex-row gap-4">
                                <div className="flex-1 relative">
                                    <input
                                        type="text"
                                        placeholder="Rechercher un document..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full px-4 py-3 pl-12 border-2 border-base-300 rounded-xl focus:border-warning focus:ring-2 focus:ring-warning/20 outline-none transition-all duration-300 bg-white"
                                    />
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 absolute left-4 top-1/2 transform -translate-y-1/2 text-base-content/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                                <button 
                                    className="btn btn-warning text-white border-none shadow-lg px-6 transition-all duration-300"
                                    onClick={() => window.location.reload()}
                                    title="Recharger tous les documents"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Actualiser
                                </button>
                            </div>
                            
                            {loading && (
                                <div className="text-center py-16">
                                    <div className="inline-block p-4 bg-warning rounded-full mb-4">
                                        <span className="loading loading-spinner loading-lg text-white"></span>
                                    </div>
                                    <p className="text-base-content text-lg font-medium">Chargement des documents...</p>
                                </div>
                            )}
                            
                            {!loading && filteredDocuments.length > 0 && (
                                <div className="space-y-4">
                                    {filteredDocuments.map((document) => (
                                        <div key={document.id} className="bg-white rounded-xl p-6 border border-base-300 hover:border-warning hover:shadow-lg transition-all duration-300 group">
                                            <div className="flex items-center gap-4">
                                                {/* Icône du document avec style warning */}
                                                <div className="flex-shrink-0 p-4 bg-warning rounded-xl shadow-sm group-hover:shadow-md group-hover:scale-105 transition-all duration-300">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                </div>

                                                {/* Informations du document */}
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="font-bold text-base-content text-xl mb-3">{document.name}</h3>
                                                    <div className="flex items-center gap-4 text-sm">
                                                        <div className="flex items-center gap-1.5 px-3 py-1 bg-warning/10 rounded-full border border-warning/20">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                            </svg>
                                                            <span className="text-warning font-medium">{document.date}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 px-3 py-1 bg-base-200 rounded-full border border-base-300">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-base-content" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                            </svg>
                                                            <span className="text-base-content font-medium">{document.size}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 px-3 py-1 bg-base-200 rounded-full border border-base-300">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-base-content" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                            </svg>
                                                            <span className="text-base-content font-medium">.{document.fileName.split('.').pop()?.toLowerCase()}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Boutons d'action avec style warning */}
                                                <div className="flex gap-2 flex-shrink-0">
                                                    <button
                                                        className="btn btn-sm btn-info text-white border-none shadow-sm hover:shadow-md transition-all duration-300 gap-2"
                                                        onClick={() => handlePreviewDocument(document)}
                                                        title="Prévisualiser le document"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                        </svg>
                                                        Voir
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-neutral text-white border-none shadow-sm hover:shadow-md transition-all duration-300 gap-2"
                                                        onClick={() => handleOpenEditModal(document)}
                                                        title="Modifier le document"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                        </svg>
                                                        Modifier
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-warning text-white border-none shadow-sm hover:shadow-md transition-all duration-300 gap-2"
                                                        onClick={() => handleDownloadDocument(document)}
                                                        title="Télécharger le document"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                        </svg>
                                                        Télécharger
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-error text-white border-none shadow-sm hover:shadow-md transition-all duration-300 gap-2"
                                                        onClick={() => handleDeleteDocument(document.relative_path || document.fileName, document.name)}
                                                        title="Supprimer le document"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                        Supprimer
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!loading && documents.length > 0 && filteredDocuments.length === 0 && (
                                <div className="text-center py-16">
                                    <div className="inline-block p-6 bg-base-200 rounded-full mb-4">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-base-content/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                    <p className="text-base-content text-lg font-medium">Aucun document trouvé</p>
                                    <p className="text-base-content/70 text-sm mt-2">Essayez avec un autre terme de recherche</p>
                                </div>
                            )}
                            
                            {!loading && documents.length === 0 && (
                                <div className="text-center py-16">
                                    <div className="inline-block p-6 bg-warning/10 rounded-full mb-4 border border-warning/20">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <p className="text-base-content text-xl font-bold mb-2">Aucun document enregistré</p>
                                    
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
            
            {/* -------------------- Panneau de Notifications (Composant Modulaire) -------------------- */}
            <NotificationPanel
                isOpen={isNotifPanelOpen}
                onClose={() => setIsNotifPanelOpen(false)}
            />

            {/* -------------------- Modal d'Aperçu de Documents -------------------- */}
            <DocumentPreviewModal
                isOpen={previewState.isOpen}
                onClose={closePreview}
                documentUrl={previewState.documentUrl}
                documentName={previewState.documentName}
                documentType={previewState.documentType}
                documentId={previewState.documentId}
            />

            {/* -------------------- Modal d'Édition de Document avec couleurs warning -------------------- */}
            {isEditModalOpen && editingDocument && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-2xl bg-white rounded-2xl shadow-2xl border-2 border-base-300">
                        {/* En-tête du modal */}
                        <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-warning">
                            <div className="p-3 bg-warning rounded-lg">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                            </div>
                            <h3 className="font-bold text-2xl text-base-content">Modifier le document</h3>
                        </div>
                        
                        <div className="space-y-6">
                            {/* Nom du document */}
                            <div>
                                <label className="block text-base-content font-semibold mb-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline mr-2 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                    </svg>
                                    Nom du document
                                </label>
                                <input
                                    type="text"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    placeholder="Entrez le nouveau nom..."
                                    className="w-full px-4 py-3 border-2 border-base-300 rounded-xl focus:border-warning focus:ring-2 focus:ring-warning/20 outline-none transition-all duration-300"
                                    disabled={uploading}
                                />
                            </div>

                            {/* Fichier actuel */}
                            <div className="p-4 bg-warning/10 rounded-xl border-l-4 border-warning">
                                <div className="flex items-start gap-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-warning flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                    <div>
                                        <p className="text-sm text-warning font-medium">Fichier actuel</p>
                                        <p className="text-base-content font-bold">{editingDocument.fileName}</p>
                                        <p className="text-xs text-base-content/70 mt-1">Taille : {editingDocument.size}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Remplacer le fichier (optionnel) */}
                            <div>
                                <label className="block text-base-content font-semibold mb-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline mr-2 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                    </svg>
                                    Remplacer le fichier (optionnel)
                                </label>
                                <div className="relative">
                                    <input
                                        type="file"
                                        accept=".docx"
                                        onChange={handleEditFileChange}
                                        className="hidden"
                                        id="edit-file-input"
                                        disabled={uploading}
                                    />
                                    <label 
                                        htmlFor="edit-file-input"
                                        className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-base-300 rounded-xl hover:border-warning hover:bg-warning/5 transition-all duration-300 cursor-pointer"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-base-content/40 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                        <span className="text-base-content/70">Cliquez pour choisir un fichier</span>
                                    </label>
                                </div>
                                {editFile && (
                                    <div className="mt-3 p-3 bg-success/10 rounded-lg border-l-4 border-success">
                                        <p className="text-success font-medium text-sm">
                                            ✓ Nouveau fichier : {editFile.name} ({(editFile.size / 1024).toFixed(2)} KB)
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Actions du modal */}
                        <div className="flex gap-3 mt-8 pt-6 border-t border-base-300">
                            <button
                                className="flex-1 btn bg-base-300 hover:bg-base-400 text-base-content border-none"
                                onClick={handleCloseEditModal}
                                disabled={uploading}
                            >
                                Annuler
                            </button>
                            <button
                                className={`flex-1 btn btn-warning text-white border-none ${uploading ? 'opacity-70' : ''}`}
                                onClick={handleSaveEdit}
                                disabled={!editName.trim() || uploading}
                            >
                                {uploading ? (
                                    <>
                                        <span className="loading loading-spinner loading-sm mr-2"></span>
                                        Enregistrement...
                                    </>
                                ) : (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Enregistrer les modifications
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                    <div className="modal-backdrop bg-base-content/50" onClick={handleCloseEditModal}></div>
                </div>
            )}
        </div>
    );
};

export default Documents;